import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Settings as SettingsIcon, Save, ToggleLeft, ToggleRight, MessageCircle, Send, Calendar, Clock4, Instagram, Facebook, MessageCircle as WhatsappIcon, Share2 } from "lucide-react";

export default function AdminSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        gst_enabled: true, gst_percent: 18,
        whatsapp_enabled: false,
        whatsapp_campaigns: {},
        whatsapp_events_enabled: {},
        social_links: { instagram: "", facebook: "", whatsapp: "" },
    });
    const [events, setEvents] = useState([]);
    const [busy, setBusy] = useState({});

    const load = async () => {
        try {
            const [{ data: s }, { data: ev }] = await Promise.all([
                api.get("/admin/app-settings"),
                api.get("/admin/whatsapp/events"),
            ]);
            setSettings({
                gst_enabled: !!s.gst_enabled,
                gst_percent: s.gst_percent ?? 18,
                whatsapp_enabled: !!s.whatsapp_enabled,
                whatsapp_campaigns: s.whatsapp_campaigns || {},
                whatsapp_events_enabled: s.whatsapp_events_enabled || {},
                social_links: { instagram: "", facebook: "", whatsapp: "", ...(s.social_links || {}) },
            });
            setEvents(ev.events || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const pct = parseInt(settings.gst_percent);
        if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("GST percent must be between 0 and 100"); return; }
        setSaving(true);
        try {
            await api.put("/admin/app-settings", {
                gst_enabled: settings.gst_enabled,
                gst_percent: pct,
                whatsapp_enabled: settings.whatsapp_enabled,
                whatsapp_campaigns: settings.whatsapp_campaigns,
                whatsapp_events_enabled: settings.whatsapp_events_enabled,
                social_links: settings.social_links,
            });
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const sendRevenue = async (period) => {
        setBusy(b => ({ ...b, [period]: true }));
        try {
            const { data } = await api.post(`/admin/notifications/send-revenue-summary?period=${period}`);
            toast.success(`${period === "daily" ? "Today's" : "This week's"} revenue summary sent to ${data.sent_to} contact(s) · Total ₹${data.summary.total_revenue}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        } finally {
            setBusy(b => ({ ...b, [period]: false }));
        }
    };

    const sendEmptyDigest = async () => {
        setBusy(b => ({ ...b, empty: true }));
        try {
            const { data } = await api.post(`/admin/notifications/send-empty-slots-digest`);
            toast.success(`Empty-slot digest sent to ${data.sent_to} contact(s) · ${data.empty_total} empty slot(s) today`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        } finally {
            setBusy(b => ({ ...b, empty: false }));
        }
    };

    const sendTest = async () => {
        try {
            await api.post(`/admin/whatsapp/test`, {});
            toast.success("Test message sent to your phone (check WhatsApp + logs)");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test failed");
        }
    };

    const updateCampaign = (key, val) => {
        setSettings(s => ({ ...s, whatsapp_campaigns: { ...s.whatsapp_campaigns, [key]: val } }));
    };
    const toggleEvent = (key) => {
        const current = settings.whatsapp_events_enabled[key];
        const next = current === false ? true : false;
        setSettings(s => ({ ...s, whatsapp_events_enabled: { ...s.whatsapp_events_enabled, [key]: next } }));
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8 max-w-4xl">
                <div className="flex items-center gap-2">
                    <SettingsIcon className="w-5 h-5 text-[#00FF66]" />
                    <h1 className="font-display text-3xl font-bold uppercase text-white">Settings</h1>
                </div>
                <p className="text-slate-400 text-sm mt-1">Platform-level toggles. Changes apply instantly after save.</p>

                {loading ? (
                    <div className="mt-8 text-slate-500 text-sm">Loading…</div>
                ) : (
                    <>
                        {/* GST CARD */}
                        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-5" data-testid="settings-card">
                            <div>
                                <h2 className="font-display text-lg font-bold uppercase text-white">GST (Goods & Services Tax)</h2>
                                <p className="text-xs text-slate-400 mt-1">
                                    Charged on top of every <span className="text-[#00FF66] font-bold">player online booking</span>.
                                    Admin & Manager manual bookings are NOT charged GST.
                                </p>
                            </div>
                            <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded p-4">
                                <div>
                                    <div className="text-sm font-bold text-white">GST Status</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{settings.gst_enabled ? "Charged on every player checkout" : "Not charged"}</div>
                                </div>
                                <button onClick={() => setSettings(s => ({ ...s, gst_enabled: !s.gst_enabled }))} data-testid="gst-toggle">
                                    {settings.gst_enabled
                                        ? <ToggleRight className="w-10 h-10 text-[#00FF66]" />
                                        : <ToggleLeft className="w-10 h-10 text-slate-600" />}
                                </button>
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">GST Percent</label>
                                <div className="mt-1 flex items-center gap-3 max-w-[200px]">
                                    <input type="number" min="0" max="100" step="1"
                                        value={settings.gst_percent}
                                        onChange={(e) => setSettings(s => ({ ...s, gst_percent: e.target.value }))}
                                        disabled={!settings.gst_enabled}
                                        data-testid="gst-percent-input"
                                        className="w-full h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white text-lg font-bold text-center disabled:opacity-50" />
                                    <span className="text-xl text-slate-400 font-bold">%</span>
                                </div>
                            </div>
                        </div>

                        {/* WHATSAPP CARD */}
                        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-5" data-testid="whatsapp-card">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h2 className="font-display text-lg font-bold uppercase text-white inline-flex items-center gap-2">
                                        <MessageCircle className="w-4 h-4 text-[#00FF66]" /> WhatsApp Notifications
                                    </h2>
                                    <p className="text-xs text-slate-400 mt-1">
                                        Powered by <span className="text-[#00FF66] font-bold">AiSensy</span>. Map each event to a pre-approved campaign name from your AiSensy dashboard.
                                    </p>
                                </div>
                                <button onClick={() => setSettings(s => ({ ...s, whatsapp_enabled: !s.whatsapp_enabled }))} data-testid="whatsapp-toggle">
                                    {settings.whatsapp_enabled
                                        ? <ToggleRight className="w-10 h-10 text-[#00FF66]" />
                                        : <ToggleLeft className="w-10 h-10 text-slate-600" />}
                                </button>
                            </div>

                            {/* Per-event configuration */}
                            <div className={`space-y-3 ${settings.whatsapp_enabled ? "" : "opacity-60 pointer-events-none"}`}>
                                {events.map((e) => {
                                    const enabled = settings.whatsapp_events_enabled[e.key] !== false; // default on
                                    return (
                                        <div key={e.key} className="bg-slate-950 border border-slate-800 rounded p-3" data-testid={`wa-event-${e.key}`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-bold text-white">{e.label}</div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">event key: <span className="text-slate-400">{e.key}</span></div>
                                                </div>
                                                <button onClick={() => toggleEvent(e.key)} data-testid={`wa-event-toggle-${e.key}`}>
                                                    {enabled
                                                        ? <ToggleRight className="w-8 h-8 text-[#00FF66]" />
                                                        : <ToggleLeft className="w-8 h-8 text-slate-600" />}
                                                </button>
                                            </div>
                                            <div className="mt-2">
                                                <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">AiSensy Campaign Name</label>
                                                <input type="text"
                                                    value={settings.whatsapp_campaigns[e.key] ?? ""}
                                                    onChange={(ev) => updateCampaign(e.key, ev.target.value)}
                                                    placeholder={e.default_campaign}
                                                    data-testid={`wa-campaign-${e.key}`}
                                                    className="w-full mt-1 h-9 px-3 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                                                <div className="text-[10px] text-slate-500 mt-1">Leave blank to use default: <code className="text-slate-400">{e.default_campaign}</code></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* On-demand actions */}
                            <div className="border-t border-slate-800 pt-4">
                                <div className="text-xs font-bold tracking-widest uppercase text-slate-400 mb-2">On-demand sends</div>
                                <div className="flex flex-wrap gap-2">
                                    <button onClick={() => sendRevenue("daily")} disabled={!settings.whatsapp_enabled || busy.daily}
                                        data-testid="send-daily-revenue-btn"
                                        className="inline-flex items-center gap-2 px-4 h-10 bg-slate-950 border border-slate-700 hover:border-[#00FF66]/60 rounded text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50">
                                        <Calendar className="w-3.5 h-3.5 text-[#00FF66]" /> {busy.daily ? "Sending…" : "Send Today's Revenue"}
                                    </button>
                                    <button onClick={() => sendRevenue("weekly")} disabled={!settings.whatsapp_enabled || busy.weekly}
                                        data-testid="send-weekly-revenue-btn"
                                        className="inline-flex items-center gap-2 px-4 h-10 bg-slate-950 border border-slate-700 hover:border-[#00FF66]/60 rounded text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50">
                                        <Calendar className="w-3.5 h-3.5 text-[#00FF66]" /> {busy.weekly ? "Sending…" : "Send This Week's Revenue"}
                                    </button>
                                    <button onClick={sendEmptyDigest} disabled={!settings.whatsapp_enabled || busy.empty}
                                        data-testid="send-empty-digest-btn"
                                        className="inline-flex items-center gap-2 px-4 h-10 bg-slate-950 border border-slate-700 hover:border-amber-400/60 rounded text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50">
                                        <Clock4 className="w-3.5 h-3.5 text-amber-400" /> {busy.empty ? "Sending…" : "Send Empty-Slot Digest Now"}
                                    </button>
                                    <button onClick={sendTest} disabled={!settings.whatsapp_enabled}
                                        data-testid="send-test-btn"
                                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66]/10 border border-[#00FF66]/40 hover:bg-[#00FF66]/20 rounded text-xs font-bold uppercase tracking-widest text-[#00FF66] disabled:opacity-50">
                                        <Send className="w-3.5 h-3.5" /> Send Test to Me
                                    </button>
                                </div>
                                <p className="text-[10px] text-slate-500 mt-2">
                                    Daily 8 AM IST empty-slot digest fires automatically when WhatsApp is enabled.
                                </p>
                            </div>
                        </div>

                        {/* SOCIAL LINKS CARD — surfaced on player About page footer */}
                        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-4" data-testid="social-links-card">
                            <div>
                                <h2 className="font-display text-lg font-bold uppercase text-white inline-flex items-center gap-2">
                                    <Share2 className="w-4 h-4 text-[#00FF66]" /> Brand Social Links
                                </h2>
                                <p className="text-xs text-slate-400 mt-1">
                                    These icons appear at the bottom of the player&apos;s About Us page. Leave a field blank to hide that icon.
                                </p>
                            </div>
                            <SocialField
                                icon={Instagram}
                                label="Instagram"
                                colorClass="text-[#E1306C]"
                                value={settings.social_links.instagram}
                                placeholder="https://instagram.com/driveorkick"
                                testid="social-instagram-input"
                                onChange={(v) => setSettings(s => ({ ...s, social_links: { ...s.social_links, instagram: v } }))}
                            />
                            <SocialField
                                icon={Facebook}
                                label="Facebook"
                                colorClass="text-[#1877F2]"
                                value={settings.social_links.facebook}
                                placeholder="https://facebook.com/driveorkick"
                                testid="social-facebook-input"
                                onChange={(v) => setSettings(s => ({ ...s, social_links: { ...s.social_links, facebook: v } }))}
                            />
                            <SocialField
                                icon={WhatsappIcon}
                                label="WhatsApp Business"
                                colorClass="text-[#25D366]"
                                value={settings.social_links.whatsapp}
                                placeholder="https://wa.me/919999999999"
                                testid="social-whatsapp-input"
                                onChange={(v) => setSettings(s => ({ ...s, social_links: { ...s.social_links, whatsapp: v } }))}
                            />
                        </div>

                        {/* SAVE BAR */}
                        <div className="mt-6 flex justify-end">
                            <button onClick={save} disabled={saving} data-testid="save-settings-btn"
                                className="inline-flex items-center gap-2 px-5 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50">
                                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

function SocialField({ icon: Icon, label, colorClass, value, placeholder, onChange, testid }) {
    return (
        <div className="bg-slate-950 border border-slate-800 rounded p-3">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded bg-slate-900 border border-slate-800">
                    <Icon className={`w-4 h-4 ${colorClass}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label} URL</label>
                    <input type="url"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        data-testid={testid}
                        className="w-full mt-0.5 h-9 px-3 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                </div>
            </div>
        </div>
    );
}
